import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';
import { Menu } from '../../models/rbac.model';
import { AuthService } from '../../services/auth.service';
import { MenuService } from '../../services/menu.service';
import { ALLOWED_ICON_NAMES, DEFAULT_ICON, SIDEBAR_ICON_REGISTRY } from './sidebar-icons';

@Component({
    selector: 'app-sidebar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, RouterLinkActive, NgTemplateOutlet],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
    readonly collapsed = input(false);
    readonly sidebarToggle = output<void>();

    menuService = inject(MenuService);
    authService = inject(AuthService);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);

    readonly mobileOpen = signal(false);

    readonly openMenus = signal<Set<number>>(new Set());

    private readonly currentPath = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => cheminSansParametres(this.router.url)),
        ),
        { initialValue: cheminSansParametres(this.router.url) },
    );

    /**
     * URL de la feuille active : la plus longue qui préfixe la page courante. Sans cela,
     * `/suivi-performance/dette` resterait actif sur `/suivi-performance/dette/suivi`.
     */
    private readonly activeLeafUrl = computed(() => {
        const path = this.currentPath();

        return urlsDesFeuilles(this.menuService.menuItems())
            .filter((url) => path === url || path.startsWith(`${url}/`))
            .reduce<string | null>((best, url) => (best && best.length >= url.length ? best : url), null);
    });

    /**
     * SECURITE: Les icones sont pre-sanitizees a partir du registre statique fige.
     * Seules les cles presentes dans ALLOWED_ICON_NAMES sont acceptees.
     */
    private readonly iconCache = new Map<string, SafeHtml>();

    readonly helpIcon = this.getSafeIcon('help');
    readonly logoutIcon = this.getSafeIcon('logout');

    isBottomMenu(menu: Menu): boolean {
        return menu.url === '/profile' || menu.label.toLowerCase().includes('profil');
    }

    isCollapsible(menu: Menu): boolean {
        return !!menu.sous_menus?.length && (!menu.url || menu.url === '#');
    }

    isSectionHeader(menu: Menu): boolean {
        return !!menu.sous_menus?.length;
    }

    /**
     * Rendu récursif (N niveaux) piloté par `menu_type` :
     *  - SECTION = séparateur de groupe (libellé statique, JAMAIS déroulant) ;
     *    ses enfants sont rendus directement, toujours visibles ;
     *  - MODULE / MENU / SUBMENU = accordéon déroulant s'il a des sous-menus,
     *    sinon une feuille (lien vers son URL).
     */
    isSection(menu: Menu): boolean {
        return menu.menu_type === 'SECTION';
    }

    hasChildren(menu: Menu): boolean {
        return !!menu.sous_menus?.length;
    }

    /** Ferme le menu mobile au clic sur une feuille (sans effet en mode desktop). */
    onLeafClick(): void {
        this.mobileOpen.set(false);
    }

    isLeafActive(url: string | null): boolean {
        return !!url && url === this.activeLeafUrl();
    }

    isMenuOpen(menuId: number): boolean {
        return this.openMenus().has(menuId);
    }

    toggleMenu(menuId: number): void {
        const current = new Set(this.openMenus());

        if (current.has(menuId)) {
            current.delete(menuId);
        } else {
            current.add(menuId);
        }
        this.openMenus.set(current);
    }

    onLogout(): void {
        this.authService.logout();
    }

    icon(iconName: string | null): SafeHtml {
        const key = iconName?.toLowerCase().trim() ?? DEFAULT_ICON;

        return this.getSafeIcon(ALLOWED_ICON_NAMES.has(key) ? key : DEFAULT_ICON);
    }

    private getSafeIcon(key: string): SafeHtml {
        const cached = this.iconCache.get(key);

        if (cached) {
            return cached;
        }
        const svg = SIDEBAR_ICON_REGISTRY[key] ?? SIDEBAR_ICON_REGISTRY[DEFAULT_ICON];
        const safe = this.sanitizer.bypassSecurityTrustHtml(svg);

        this.iconCache.set(key, safe);

        return safe;
    }
}

function cheminSansParametres(url: string): string {
    return url.split(/[?#;]/)[0];
}

function urlsDesFeuilles(menus: readonly Menu[]): string[] {
    return menus.flatMap((menu) => (menu.sous_menus?.length ? urlsDesFeuilles(menu.sous_menus) : menu.url && menu.url !== '#' ? [menu.url] : []));
}
